import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import "./tasks.css";

export default function Tasks() {
  const [punchDetails, setPunchDetails] = useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("accessToken");

  const navigate = useNavigate();

  // Attendance fetch (unchanged)
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!userId || !token) return;

      const todayDate = new Date().toISOString().split("T")[0];

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/api/attendance/single-attendance/`,
          {
            params: { punch_date: todayDate, employee__id: userId },
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const attendance = response.data?.[0];
        if (attendance?.punch_details?.length > 0) {
          setPunchDetails(attendance.punch_details.slice(0, 3));
        } else {
          setPunchDetails([]);
        }
      } catch (err) {
        setPunchDetails([]);
      }
    };

    fetchAttendance();
  }, [userId, token]);

  // Leaves fetch + filter
  useEffect(() => {
    const fetchLeaveData = async () => {
      if (!userId || !token) {
        setLeaveData([]);
        setLoading(false);
        return;
      }

      const monthStr = dayjs().format("MM-YYYY"); // e.g. 01-2026

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/api/leavemanagement/leaves/employee/${userId}/?date=${monthStr}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        let leaves = [];
        if (Array.isArray(response.data)) {
          leaves = response.data;
        } else if (response.data?.leaves) {
          leaves = response.data.leaves;
        }

        // ────────────────────────────────────────────────
        // Filter: remove unwanted / auto-generated leave types
        const unwanted = [
          "loss of pay",
          "lop",
          "lwp",
          "unpaid",
          "absent",
          "no punch",
          "not punched",
          "uncharged",     // in case this exists
          // Add more exact or partial strings if needed
        ];

        const filtered = leaves.filter((leave) => {
          const type = (leave.leave_type || "").toLowerCase().trim();
          // Skip if type contains/matches any unwanted phrase
          return !unwanted.some((bad) => type.includes(bad));
        });
        // ────────────────────────────────────────────────

        setLeaveData(filtered);
      } catch (err) {
        console.error("Leave fetch error:", err);
        setLeaveData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveData();
  }, [userId, token]);

  const handleViewAll = () => {
    navigate(`/user/dayList/${userId}`);
  };

  // Helper to format date from YYYY-MM-DD → DD-MM-YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return dayjs(dateStr).format("DD-MM-YYYY");
  };

  return (
    <div className="taskConatiner d-flex gap-3">
      {/* Punch / Attendance section – unchanged */}
      <div className="MainTasks">
        <div className="overviewHeader d-flex justify-content-between align-items-center">
          <h6>Time Details</h6>
        </div>
        <div className="Taskbody">
          <table className="w-100">
            <thead>
              <tr>
                <th>Time</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  <tr style={{ height: "35px" }}>
                    <td className="skeleton-loader"></td>
                    <td className="skeleton-loader"></td>
                  </tr>
                  <tr style={{ height: "35px" }}>
                    <td className="skeleton-loader"></td>
                    <td className="skeleton-loader"></td>
                  </tr>
                  <tr style={{ height: "35px" }}>
                    <td className="skeleton-loader"></td>
                    <td className="skeleton-loader"></td>
                  </tr>
                </>
              ) : punchDetails.length > 0 ? (
                punchDetails.map((punch, index) => (
                  <tr key={index} style={{ height: "35px" }}>
                    <td>{punch.punch_time}</td>
                    <td>
                      {punch.punch_direction === "clock-in"
                        ? "Check-in"
                        : "Check-out"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr style={{ height: "105px" }}>
                  <td colSpan="2" className="text-center">
                    No punch details today
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div
          className="taskFooter d-flex justify-content-end ps-3 pt-2 pe-2"
          style={{
            height: "30px",
            fontWeight: "500",
            fontSize: "clamp(13px, 1.3vw, 15px)",
          }}
        >
          {punchDetails.length > 0 && !loading && (
            <div style={{ cursor: "pointer" }} onClick={handleViewAll}>
              View All
            </div>
          )}
        </div>
      </div>

      {/* My Leaves section – now filtered */}
      <div className="MainGraphs">
        <div className="overviewBody w-100">
          <div className="overviewHeader d-flex w-100 justify-content-between align-items-center">
            <h6>
              My Leaves
            </h6>
            {leaveData.length > 0 && !loading && (
              <Link
                to="/user/selfleave"
                className="text-decoration-none small"
                style={{ color: "var(--primary-color)", fontWeight: "600", whiteSpace: "nowrap" }}
              >
                View all
              </Link>
            )}
          </div>

          <div className="px-2 py-1">
            {loading ? (
              <>
                <div className="skeleton-loader my-2" style={{ height: "50px" }}></div>
                <div className="skeleton-loader my-2" style={{ height: "50px" }}></div>
              </>
            ) : leaveData.length > 0 ? (
              leaveData.slice(0, 2).map((leave, index) => (
                <div
                  key={index}
                  className="leave-item border-bottom py-2"
                  style={{ fontSize: "clamp(12.5px, 1.2vw, 14px)" }}
                >
                  <div className="d-flex justify-content-between mb-1">
                    <strong>{formatDate(leave?.start_date)}</strong>
                    <span className="text-muted">
                      {leave.total_leave_days || 0}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>{leave.leave_type || "Leave"}</span>
                    <span
                      className={
                        leave.status === "Approved"
                          ? "text-success"
                          : leave.status === "Pending"
                            ? "text-warning"
                            : "text-danger"
                      }
                    >
                      {leave?.status || "Unknown"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div
                className="text-center text-muted py-4"
                style={{ minHeight: "110px" }}
              >
                No leaves this month
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}